import {
  DUNGEON_MAX_MAP_PAYLOAD_BYTES,
  DUNGEON_MAX_RENDER_PLACEMENTS,
  type DungeonBlueprint,
  type DungeonConnection,
  type DungeonDirection,
  type DungeonGenerationFailure,
  type DungeonMetrics,
  type DungeonRegion,
  type DungeonRoom,
  type GeneratedDungeon,
  type GenerateDungeonOptions,
} from './dungeon';
import {
  dungeonStructurePieceForRole,
  dungeonStructureTerrainAction,
  SHAME_STRUCTURE_KIT,
} from './dungeonStructureKit';
import { compileDungeonStructures } from './dungeonStructureCompiler';
import { MAP_VOID_TILE, type MapData, type StaticPlacement } from './map';
import type { TilePoint } from './mobile';
import { SeededRng } from './rng';

export const SHAME_DUNGEON_GENERATOR_VERSION = 1;
export const SHAME_DUNGEON_WIDTH = 256;
export const SHAME_DUNGEON_HEIGHT = 256;
export const SHAME_DUNGEON_GRID_SIZE = 7;
export const SHAME_DUNGEON_ROOM_COUNT =
  SHAME_DUNGEON_GRID_SIZE * SHAME_DUNGEON_GRID_SIZE;
export const SHAME_DUNGEON_PASSAGE_WIDTHS = [3, 3, 3, 3, 5, 5, 7] as const;

const ROOM_SIZES = [13, 15, 17, 19] as const;
const GRID_ORIGIN = 32;
const GRID_PITCH = 32;
const ROOM_JITTER = 10;
const EXTRA_LOOP_COUNT = 10;
const IDENTITY = { rotation: 0 as const, mirrored: false };

interface GridRoom {
  index: number;
  column: number;
  row: number;
  center: TilePoint;
  width: number;
  height: number;
  footprint: TilePoint[];
}

interface GridEdge {
  a: number;
  b: number;
}

function pointKey(point: TilePoint): string {
  return `${point.x},${point.y}`;
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

function shuffled<T>(values: readonly T[], rng: SeededRng): T[] {
  const result = [...values];
  for (let index = result.length - 1; index > 0; index--) {
    const other = rng.nextInt(index + 1);
    [result[index], result[other]] = [result[other]!, result[index]!];
  }
  return result;
}

function gridNeighbours(index: number): number[] {
  const column = index % SHAME_DUNGEON_GRID_SIZE;
  const row = Math.floor(index / SHAME_DUNGEON_GRID_SIZE);
  const neighbours: number[] = [];
  if (row > 0) neighbours.push(index - SHAME_DUNGEON_GRID_SIZE);
  if (column + 1 < SHAME_DUNGEON_GRID_SIZE) neighbours.push(index + 1);
  if (row + 1 < SHAME_DUNGEON_GRID_SIZE) neighbours.push(index + SHAME_DUNGEON_GRID_SIZE);
  if (column > 0) neighbours.push(index - 1);
  return neighbours;
}

function planGridEdges(rng: SeededRng, entranceIndex: number): GridEdge[] {
  const visited = new Set([entranceIndex]);
  const edges: GridEdge[] = [];
  const frontier = gridNeighbours(entranceIndex).map((candidate) => ({
    a: entranceIndex,
    b: candidate,
  }));
  while (visited.size < SHAME_DUNGEON_ROOM_COUNT) {
    const candidates = frontier.filter((edge) => !visited.has(edge.b));
    const next = candidates[rng.nextInt(candidates.length)]!;
    edges.push(next);
    visited.add(next.b);
    for (const candidate of gridNeighbours(next.b)) {
      if (!visited.has(candidate)) frontier.push({ a: next.b, b: candidate });
    }
  }

  const existing = new Set(edges.map((edge) => edgeKey(edge.a, edge.b)));
  const degrees = new Array<number>(SHAME_DUNGEON_ROOM_COUNT).fill(0);
  for (const edge of edges) {
    degrees[edge.a]! += 1;
    degrees[edge.b]! += 1;
  }
  const protectedLeaves = new Set(
    shuffled(
      degrees
        .map((degree, index) => ({ degree, index }))
        .filter(({ degree }) => degree === 1)
        .map(({ index }) => index),
      rng,
    ).slice(0, 5),
  );
  const loopCandidates = shuffled(
    Array.from({ length: SHAME_DUNGEON_ROOM_COUNT }, (_, index) =>
      gridNeighbours(index)
        .filter((candidate) => candidate > index)
        .map((candidate) => ({ a: index, b: candidate })),
    ).flat().filter((edge) => !existing.has(edgeKey(edge.a, edge.b))),
    rng,
  );
  for (const edge of loopCandidates) {
    if (edges.length >= SHAME_DUNGEON_ROOM_COUNT - 1 + EXTRA_LOOP_COUNT) break;
    if (protectedLeaves.has(edge.a) || protectedLeaves.has(edge.b)) continue;
    edges.push(edge);
    degrees[edge.a]! += 1;
    degrees[edge.b]! += 1;
  }
  return edges;
}

function roomFootprint(center: TilePoint, width: number, height: number): TilePoint[] {
  const left = center.x - Math.floor(width / 2);
  const top = center.y - Math.floor(height / 2);
  const radiusX = Math.floor(width / 2);
  const radiusY = Math.floor(height / 2);
  return Array.from({ length: width * height }, (_, index) => {
    const x = left + (index % width);
    const y = top + Math.floor(index / width);
    return { x, y };
  }).filter((point) => {
    const dx = Math.abs(point.x - center.x);
    const dy = Math.abs(point.y - center.y);
    const inset = dy === radiusY ? 2 : dy === radiusY - 1 ? 1 : 0;
    return dx <= radiusX - inset;
  });
}

function createRooms(rng: SeededRng): GridRoom[] {
  const rooms: GridRoom[] = [];
  for (let index = 0; index < SHAME_DUNGEON_ROOM_COUNT; index++) {
    const column = index % SHAME_DUNGEON_GRID_SIZE;
    const row = Math.floor(index / SHAME_DUNGEON_GRID_SIZE);
    let selected:
      | Pick<GridRoom, 'center' | 'width' | 'height' | 'footprint'>
      | undefined;
    for (let attempt = 0; attempt < 128 && selected === undefined; attempt++) {
      const center = {
        x: GRID_ORIGIN + column * GRID_PITCH +
          rng.nextInt(ROOM_JITTER * 2 + 1) - ROOM_JITTER,
        y: GRID_ORIGIN + row * GRID_PITCH +
          rng.nextInt(ROOM_JITTER * 2 + 1) - ROOM_JITTER,
      };
      const width = ROOM_SIZES[rng.nextInt(ROOM_SIZES.length)]!;
      const height = ROOM_SIZES[rng.nextInt(ROOM_SIZES.length)]!;
      const clearance = 3;
      const overlaps = rooms.some((room) =>
        Math.abs(room.center.x - center.x) <
          Math.floor(room.width / 2) + Math.floor(width / 2) + clearance &&
        Math.abs(room.center.y - center.y) <
          Math.floor(room.height / 2) + Math.floor(height / 2) + clearance,
      );
      if (!overlaps) {
        selected = {
          center,
          width,
          height,
          footprint: roomFootprint(center, width, height),
        };
      }
    }
    selected ??= {
      center: {
        x: GRID_ORIGIN + column * GRID_PITCH,
        y: GRID_ORIGIN + row * GRID_PITCH,
      },
      width: ROOM_SIZES[0],
      height: ROOM_SIZES[0],
      footprint: roomFootprint(
        {
          x: GRID_ORIGIN + column * GRID_PITCH,
          y: GRID_ORIGIN + row * GRID_PITCH,
        },
        ROOM_SIZES[0],
        ROOM_SIZES[0],
      ),
    };
    rooms.push({
      index,
      column,
      row,
      ...selected,
    });
  }
  return rooms;
}

function direction(from: TilePoint, to: TilePoint): DungeonDirection {
  if (from.x === to.x) return from.y < to.y ? 'south' : 'north';
  return from.x < to.x ? 'east' : 'west';
}

function opposite(value: DungeonDirection): DungeonDirection {
  switch (value) {
    case 'north': return 'south';
    case 'east': return 'west';
    case 'south': return 'north';
    case 'west': return 'east';
  }
}

function segmentRegion(
  from: TilePoint,
  to: TilePoint,
  passageWidth: number,
): DungeonRegion {
  const half = Math.floor(passageWidth / 2);
  return from.y === to.y
    ? {
        x: Math.min(from.x, to.x),
        y: from.y - half,
        width: Math.abs(to.x - from.x) + 1,
        height: passageWidth,
      }
    : {
        x: from.x - half,
        y: Math.min(from.y, to.y),
        width: passageWidth,
        height: Math.abs(to.y - from.y) + 1,
      };
}

function regionCells(region: DungeonRegion): TilePoint[] {
  return Array.from({ length: region.width * region.height }, (_, index) => ({
    x: region.x + (index % region.width),
    y: region.y + Math.floor(index / region.width),
  }));
}

function connectionSegments(
  from: TilePoint,
  to: TilePoint,
  horizontalFirst: boolean,
): Array<{ from: TilePoint; to: TilePoint; direction: DungeonDirection }> {
  if (from.x === to.x || from.y === to.y) {
    return [{ from, to, direction: direction(from, to) }];
  }
  const corner = horizontalFirst
    ? { x: to.x, y: from.y }
    : { x: from.x, y: to.y };
  return [
    { from, to: corner, direction: direction(from, corner) },
    { from: corner, to, direction: direction(corner, to) },
  ];
}

function adjacency(edges: readonly GridEdge[]): Map<number, number[]> {
  const result = new Map(
    Array.from({ length: SHAME_DUNGEON_ROOM_COUNT }, (_, index) => [index, [] as number[]]),
  );
  for (const edge of edges) {
    result.get(edge.a)!.push(edge.b);
    result.get(edge.b)!.push(edge.a);
  }
  return result;
}

function distances(graph: ReadonlyMap<number, readonly number[]>, start: number): Map<number, number> {
  const result = new Map([[start, 0]]);
  const pending = [start];
  while (pending.length > 0) {
    const current = pending.shift()!;
    for (const next of graph.get(current) ?? []) {
      if (result.has(next)) continue;
      result.set(next, result.get(current)! + 1);
      pending.push(next);
    }
  }
  return result;
}

function shortestPath(
  graph: ReadonlyMap<number, readonly number[]>,
  start: number,
  goal: number,
): number[] {
  const previous = new Map<number, number | null>([[start, null]]);
  const pending = [start];
  while (pending.length > 0) {
    const current = pending.shift()!;
    if (current === goal) break;
    for (const next of graph.get(current) ?? []) {
      if (previous.has(next)) continue;
      previous.set(next, current);
      pending.push(next);
    }
  }
  const path: number[] = [];
  for (let current: number | null = goal; current !== null; current = previous.get(current) ?? null) {
    path.push(current);
  }
  return path.reverse();
}

function chooseFloorTile(x: number, y: number, seed: number): string {
  const variants = SHAME_STRUCTURE_KIT.floorVariants ?? [
    { tileName: SHAME_STRUCTURE_KIT.floor, weight: 1 },
  ];
  const total = variants.reduce((sum, variant) => sum + variant.weight, 0);
  let hash = (
    Math.imul(x + 17, 73_856_093) ^
    Math.imul(y + 31, 19_349_663) ^
    seed
  ) >>> 0;
  hash ^= hash >>> 16;
  let draw = hash % total;
  for (const variant of variants) {
    if (draw < variant.weight) return variant.tileName;
    draw -= variant.weight;
  }
  return variants.at(-1)!.tileName;
}

function roomRegion(room: GridRoom): DungeonRegion {
  return {
    x: room.center.x - Math.floor(room.width / 2),
    y: room.center.y - Math.floor(room.height / 2),
    width: room.width,
    height: room.height,
  };
}

function revealRows(points: readonly TilePoint[]): DungeonRegion[] {
  const byY = new Map<number, number[]>();
  for (const point of points) {
    const xs = byY.get(point.y) ?? [];
    xs.push(point.x);
    byY.set(point.y, xs);
  }
  return [...byY]
    .sort((a, b) => a[0] - b[0])
    .map(([y, xs]) => ({
      x: Math.min(...xs),
      y,
      width: Math.max(...xs) - Math.min(...xs) + 1,
      height: 1,
    }));
}

function generationFailure(
  seed: number,
  constraint: string,
  attempts: number,
): DungeonGenerationFailure {
  return {
    ok: false,
    seed,
    generatorVersion: SHAME_DUNGEON_GENERATOR_VERSION,
    attempts,
    constraint,
    stage: 'assembly',
  };
}

function compileShameStructures(floor: readonly TilePoint[]) {
  const initial = compileDungeonStructures({ floor });
  if (initial.ok) return initial;
  const posts = new Map<string, TilePoint>();
  for (const error of initial.errors) {
    const match =
      /^(?:ambiguous-diagonal-boundary|ambiguous-opposite-boundary|undeclared-post-boundary):(-?\d+),(-?\d+)/.exec(
        error,
      );
    if (!match) return initial;
    const point = { x: Number(match[1]), y: Number(match[2]) };
    posts.set(pointKey(point), point);
  }
  return compileDungeonStructures({ floor, posts: [...posts.values()] });
}

function mixedSeed(seed: number, attempt: number): number {
  let value = (seed ^ Math.imul(attempt + 1, 0x9e3779b1)) >>> 0;
  value ^= value >>> 16;
  value = Math.imul(value, 0x85ebca6b) >>> 0;
  value ^= value >>> 13;
  return value >>> 0;
}

function generateShameDungeonAttempt(
  seed: number,
  attempt: number,
): GeneratedDungeon | DungeonGenerationFailure {
  const attempts = attempt + 1;
  const rng = new SeededRng(mixedSeed(seed, attempt));
  const cornerIndexes = [
    0,
    SHAME_DUNGEON_GRID_SIZE - 1,
    SHAME_DUNGEON_ROOM_COUNT - SHAME_DUNGEON_GRID_SIZE,
    SHAME_DUNGEON_ROOM_COUNT - 1,
  ];
  const entranceIndex = cornerIndexes[rng.nextInt(cornerIndexes.length)]!;
  const rooms = createRooms(rng);
  const edges = planGridEdges(rng, entranceIndex);
  const graph = adjacency(edges);
  const entranceDistances = distances(graph, entranceIndex);
  const exitIndex = [...entranceDistances].sort(
    (a, b) => b[1] - a[1] || a[0] - b[0],
  )[0]![0];
  const deadEnds = [...graph]
    .filter(([index, neighbours]) =>
      index !== entranceIndex && index !== exitIndex && neighbours.length === 1,
    )
    .sort((a, b) =>
      (entranceDistances.get(b[0]) ?? 0) - (entranceDistances.get(a[0]) ?? 0) ||
      a[0] - b[0],
    );
  const landmarks = new Set(deadEnds.slice(0, 3).map(([index]) => index));

  const blueprintRooms: DungeonRoom[] = rooms.map((room) => {
    const neighbours = graph.get(room.index)!;
    const role =
      room.index === entranceIndex
        ? 'entrance'
        : room.index === exitIndex
          ? 'exit'
          : landmarks.has(room.index)
            ? 'landmark'
            : neighbours.length >= 3
              ? 'junction'
              : neighbours.length === 1
                ? 'dead-end'
                : 'ordinary';
    const region = roomRegion(room);
    const revealRegions = revealRows(room.footprint);
    const reservedSize = Math.min(5, room.width - 4, room.height - 4);
    return {
      id: `shame-room-${room.index}`,
      moduleId: 'shame-chamber-v1',
      role,
      grid: { x: room.column, y: room.row },
      region,
      revealRegion: region,
      revealRegions,
      footprint: room.footprint.map((point) => ({ ...point })),
      sizeClass: room.width >= 19 || room.height >= 19 ? 'large' : 'standard',
      reservedZones: [{
        x: room.center.x - Math.floor(reservedSize / 2),
        y: room.center.y - Math.floor(reservedSize / 2),
        width: reservedSize,
        height: reservedSize,
      }],
      encounterZones: [{
        x: room.center.x - 2,
        y: region.y + 2,
        width: 5,
        height: 3,
      }],
      transform: { ...IDENTITY },
      ...(landmarks.has(room.index) ? { landmark: `shame-landmark-${room.index}` } : {}),
    };
  });

  const connectionFloor = new Map<string, TilePoint>();
  const blueprintConnections: DungeonConnection[] = edges.map((edge, index) => {
    const a = rooms[edge.a]!;
    const b = rooms[edge.b]!;
    const passageWidth =
      SHAME_DUNGEON_PASSAGE_WIDTHS[rng.nextInt(SHAME_DUNGEON_PASSAGE_WIDTHS.length)]!;
    const routed = connectionSegments(a.center, b.center, rng.nextInt(2) === 0);
    const revealRegions = routed.map((segment) =>
      segmentRegion(segment.from, segment.to, passageWidth));
    const footprintByKey = new Map<string, TilePoint>();
    for (const region of revealRegions) {
      for (const point of regionCells(region)) {
        footprintByKey.set(pointKey(point), point);
        connectionFloor.set(pointKey(point), point);
      }
    }
    const footprint = [...footprintByKey.values()].sort(
      (left, right) => left.y - right.y || left.x - right.x,
    );
    const revealRegion = {
      x: Math.min(...revealRegions.map((region) => region.x)),
      y: Math.min(...revealRegions.map((region) => region.y)),
      width:
        Math.max(...revealRegions.map((region) => region.x + region.width)) -
        Math.min(...revealRegions.map((region) => region.x)),
      height:
        Math.max(...revealRegions.map((region) => region.y + region.height)) -
        Math.min(...revealRegions.map((region) => region.y)),
    };
    return {
      id: `shame-connection-${index}`,
      moduleId: 'shame-hallway-v1',
      passageWidth,
      region: revealRegion,
      transform: { ...IDENTITY },
      a: {
        roomId: `shame-room-${edge.a}`,
        socketId: `to-${edge.b}`,
        direction: routed[0]!.direction,
        position: { ...a.center },
        compatibility: 'shame-cave-v1',
      },
      b: {
        roomId: `shame-room-${edge.b}`,
        socketId: `to-${edge.a}`,
        direction: opposite(routed.at(-1)!.direction),
        position: { ...b.center },
        compatibility: 'shame-cave-v1',
      },
      revealRegion,
      revealRegions,
      segments: routed.map((segment, segmentIndex) => ({
        ...segment,
        moduleId: 'shame-hallway-v1',
        transform: { ...IDENTITY },
        region: revealRegions[segmentIndex]!,
      })),
      footprint,
    };
  });

  const floor = new Map<string, TilePoint>();
  for (const room of rooms) {
    for (const point of room.footprint) floor.set(pointKey(point), point);
  }
  for (const [key, point] of connectionFloor) floor.set(key, point);
  const compiled = compileShameStructures([...floor.values()]);
  if (!compiled.ok) {
    return generationFailure(
      seed,
      `shame-structure-compile:${compiled.errors.join(';')}`,
      attempts,
    );
  }

  const floorVariants = SHAME_STRUCTURE_KIT.floorVariants ?? [
    { tileName: SHAME_STRUCTURE_KIT.floor, weight: 1 },
  ];
  const palette = [
    MAP_VOID_TILE,
    ...new Set([
      ...floorVariants.map((variant) => variant.tileName),
      ...Object.values(SHAME_STRUCTURE_KIT.innerCorners)
        .map((piece) => piece.voidUnderlay)
        .filter((tileName): tileName is string => tileName !== undefined),
      ...Object.values(SHAME_STRUCTURE_KIT.doorwayReturns)
        .map((piece) => piece.voidUnderlay)
        .filter((tileName): tileName is string => tileName !== undefined),
    ]),
  ];
  const paletteIndexes = new Map(palette.map((tileName, index) => [tileName, index]));
  const cells = new Array<number>(SHAME_DUNGEON_WIDTH * SHAME_DUNGEON_HEIGHT).fill(0);
  const blocked = new Array<boolean>(SHAME_DUNGEON_WIDTH * SHAME_DUNGEON_HEIGHT).fill(true);
  for (const point of floor.values()) {
    const cell = point.y * SHAME_DUNGEON_WIDTH + point.x;
    cells[cell] = paletteIndexes.get(chooseFloorTile(point.x, point.y, seed))!;
    blocked[cell] = false;
  }

  const statics: StaticPlacement[] = [];
  for (const placement of compiled.placements) {
    const piece = dungeonStructurePieceForRole(placement.role, IDENTITY, SHAME_STRUCTURE_KIT);
    const cell = placement.position.y * SHAME_DUNGEON_WIDTH + placement.position.x;
    const terrain = dungeonStructureTerrainAction(piece, cells[cell] === 0);
    if (terrain?.kind === 'void') cells[cell] = 0;
    if (terrain?.kind === 'fill') cells[cell] = paletteIndexes.get(terrain.tileName)!;
    blocked[cell] = true;
    statics.push({
      tileName: piece.tileName,
      x: placement.position.x,
      y: placement.position.y,
      z: 0,
    });
  }

  const entrance = rooms[entranceIndex]!;
  const exit = rooms[exitIndex]!;
  const blueprint: DungeonBlueprint = {
    generatorId: 'shame',
    seed,
    generatorVersion: SHAME_DUNGEON_GENERATOR_VERSION,
    rooms: blueprintRooms,
    connections: blueprintConnections,
    entrance: {
      roomId: `shame-room-${entranceIndex}`,
      position: { ...entrance.center },
    },
    exit: {
      roomId: `shame-room-${exitIndex}`,
      position: { ...exit.center },
    },
    landmarks: blueprintRooms
      .filter((room) => room.role === 'landmark')
      .map((room) => ({
        roomId: room.id,
        position: {
          x: room.region.x + Math.floor(room.region.width / 2),
          y: room.region.y + Math.floor(room.region.height / 2),
        },
      })),
  };
  const mainRoute = shortestPath(graph, entranceIndex, exitIndex);
  let mainRouteTurns = 0;
  let priorDirection: DungeonDirection | undefined;
  for (let index = 1; index < mainRoute.length; index++) {
    const currentDirection = direction(
      rooms[mainRoute[index - 1]!]!.center,
      rooms[mainRoute[index]!]!.center,
    );
    if (priorDirection !== undefined && currentDirection !== priorDirection) mainRouteTurns += 1;
    priorDirection = currentDirection;
  }
  const degreeValues = [...graph.values()].map((neighbours) => neighbours.length);
  const metrics: DungeonMetrics = {
    mainRouteDepth: entranceDistances.get(exitIndex)!,
    mainRouteTurns,
    mainRouteBranchCount: mainRoute.filter((index) => graph.get(index)!.length >= 3).length,
    branchCount: degreeValues.filter((degree) => degree >= 3).length,
    independentLoops: edges.length - rooms.length + 1,
    shortestLoopLength: 4,
    deadEndCount: degreeValues.filter((degree) => degree === 1).length - 2,
    maxDeadEndDepth: 1,
    rewardedDeadEndCount: landmarks.size,
    minLandmarkSpacing: Math.min(
      ...[...landmarks].flatMap((a, index, values) =>
        values.slice(index + 1).map((b) => distances(graph, a).get(b) ?? 0),
      ),
    ),
    backtrackingBurden: degreeValues.filter((degree) => degree === 1).length * 2,
    packingBacktracks: 0,
    moduleRepetition: 1,
    compactness: rooms.length / (SHAME_DUNGEON_GRID_SIZE ** 2),
    unrelatedAxisAlignment: 0,
  };
  const map: MapData = {
    width: SHAME_DUNGEON_WIDTH,
    height: SHAME_DUNGEON_HEIGHT,
    palette,
    cells,
    blocked,
    statics,
    playerSpawn: { ...entrance.center },
    lighting: 'dungeon',
    dungeon: blueprint,
  };
  if (new TextEncoder().encode(JSON.stringify(map)).byteLength > DUNGEON_MAX_MAP_PAYLOAD_BYTES) {
    return generationFailure(seed, 'shame-payload-budget', attempts);
  }
  if (floor.size + statics.length > DUNGEON_MAX_RENDER_PLACEMENTS) {
    return generationFailure(seed, 'shame-render-budget', attempts);
  }
  return {
    ok: true,
    seed,
    generatorVersion: SHAME_DUNGEON_GENERATOR_VERSION,
    attempts,
    map,
    blueprint,
    metrics,
  };
}

export function generateShameDungeon(
  options: Pick<GenerateDungeonOptions, 'seed'>,
): GeneratedDungeon | DungeonGenerationFailure {
  const seed = options.seed >>> 0;
  let lastFailure = generationFailure(seed, 'shame-generation-attempts-exhausted', 0);
  for (let attempt = 0; attempt < 16; attempt++) {
    const generated = generateShameDungeonAttempt(seed, attempt);
    if (generated.ok) return generated;
    lastFailure = generated;
  }
  return lastFailure;
}
